import React from "react";
import {
  fetchMunicipalitiesWithLines,
  fetchZonesForLines,
} from "../../../../api/zones";
import {
  Alert,
  AlertTitle,
  AlertDescription,
  MultiSelect,
  Spinner,
  Label,
  Button,
  Heading,
} from "@ogt/ui";
import { selectionSummary } from "../../../../utils/selectionSummary";
import { useZones } from "../../../../hooks/useZones";
import type { MunicipalityWithLines } from "../../../../api/zones";

interface ZoneSectionProps {
  style?: React.CSSProperties;
  className?: string;
}

// Section that shows municipalities and allows selecting entire lines (which
// expands to their zones via the global zone state hooks).
export function ZoneSection({ style, className }: ZoneSectionProps) {
  const { selectedZones, setActiveZoneId, addAndActivateZones, removeZones } =
    useZones();

  const [municipalities, setMunicipalities] = React.useState<
    MunicipalityWithLines[]
  >([]);
  const [loadingMunicipalities, setLoadingMunicipalities] =
    React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [loadingLine, setLoadingLine] = React.useState<Record<number, boolean>>(
    {}
  );

  const baseId = React.useId();

  React.useEffect(() => {
    (async () => {
      setLoadingMunicipalities(true);
      setError(null);
      try {
        const data = await fetchMunicipalitiesWithLines();
        setMunicipalities(data);
      } catch (e) {
        console.error("Misslyckades hämta kommuner med linjer:", e);
        setError("Kunde inte ladda kommuner och linjer just nu.");
      } finally {
        setLoadingMunicipalities(false);
      }
    })();
  }, []);

  // fetched municipality data; fall back to the id string if not found.
  const getDisplayName = React.useCallback(
    (id: number | string) => {
      const s = String(id);
      for (const m of municipalities) {
        const hit = m.Value.find((v) => String(v.LineNrReal) === s);
        if (hit) {
          return hit.Ogt?.OgtLineNamePreformatted ?? hit.LineName ?? s;
        }
      }
      return s;
    },
    [municipalities]
  );

  // Derive which lines are currently selected from the global zone state (Jotai).
  const checkedLines = React.useMemo(() => {
    const set = new Set<number>();
    for (const z of selectedZones) {
      const [lnStr] = z.id.split("_");
      const ln = Number(lnStr);
      if (!Number.isNaN(ln)) set.add(ln);
    }
    return set;
  }, [selectedZones]);

  // Toggle handler for selecting/deselecting a whole line.
  async function handleLineToggle(
    lineNrReal: number,
    displayName: string,
    nextChecked: boolean
  ) {
    if (loadingLine[lineNrReal]) return;
    setLoadingLine((s) => ({ ...s, [lineNrReal]: true }));
    try {
      if (nextChecked) {
        const raw = await fetchZonesForLines([lineNrReal]);
        const zones = raw;
        addAndActivateZones(zones);
        if (zones.length) setActiveZoneId(zones[0].id);
      } else {
        const ids = selectedZones
          .filter((z) => z.id.startsWith(`${lineNrReal}_`))
          .map((z) => z.id);
        removeZones(ids);
      }
    } finally {
      setLoadingLine((s) => ({ ...s, [lineNrReal]: false }));
    }
  }

  // Build MultiSelect option objects for a municipality's lines.
  function buildOptionsForMunicipality(m: MunicipalityWithLines) {
    const opts: Array<{
      value: string;
      label: string;
      disabled?: boolean;
      keywords?: string;
    }> = [];
    for (let i = 0; i < m.Value.length; i++) {
      const line = m.Value[i];
      const id = String(line.LineNrReal);
      const isLoading = !!loadingLine[line.LineNrReal];

      const preformatted =
        line.Ogt?.OgtLineNamePreformatted ?? line.LineName ?? id;

      opts.push({
        value: id,
        label: preformatted,
        disabled: isLoading,
        keywords: `${m.Key} ${line.LineName} ${
          line.Ogt?.OgtLineNamePreformatted ?? ""
        }`,
      });
    }
    return opts;
  }

  // Which values should be selected in the MultiSelect for a municipality.
  function selectedValuesForMunicipality(m: MunicipalityWithLines) {
    const out: string[] = [];
    for (const ln of m.Value) {
      if (checkedLines.has(ln.LineNrReal)) out.push(String(ln.LineNrReal));
    }
    return out;
  }

  // Compare previous vs next selection and trigger toggles for differences.
  function onMunicipalityChange(
    m: MunicipalityWithLines,
    nextValues: string[]
  ) {
    // compare previous vs next; trigger toggles for the diff
    const prevValues = selectedValuesForMunicipality(m);

    const prevSet: Record<string, true> = {};
    for (let i = 0; i < prevValues.length; i++) prevSet[prevValues[i]] = true;
    const nextSet: Record<string, true> = {};
    for (let i = 0; i < nextValues.length; i++) nextSet[nextValues[i]] = true;

    const added: string[] = [];
    const removed: string[] = [];

    for (let i = 0; i < nextValues.length; i++) {
      const v = nextValues[i];
      if (!prevSet[v]) added.push(v);
    }
    for (let i = 0; i < prevValues.length; i++) {
      const v = prevValues[i];
      if (!nextSet[v]) removed.push(v);
    }

    for (let i = 0; i < added.length; i++) {
      const idNum = Number(added[i]);
      if (!loadingLine[idNum]) {
        void handleLineToggle(idNum, getDisplayName(idNum), true);
      }
    }

    for (let i = 0; i < removed.length; i++) {
      const idNum = Number(removed[i]);
      if (!loadingLine[idNum]) {
        void handleLineToggle(idNum, getDisplayName(idNum), false);
      }
    }
  }

  // Check if any line in the municipality is currently loading.
  function isMunicipalityLoading(m: MunicipalityWithLines) {
    for (let i = 0; i < m.Value.length; i++) {
      if (loadingLine[m.Value[i].LineNrReal]) return true;
    }
    return false;
  }

  function handleClearZones() {
    const zoneIds = selectedZones.map((zone) => zone.id);

    if (zoneIds.length === 0) return;

    removeZones(zoneIds);
    setActiveZoneId(null);
  }

  const [activeMunicipalityKey, setActiveMunicipalityKey] = React.useState<
    string | null
  >(null);

  // Attach focus/blur handlers to track which municipality block is active
  // (used for conditionally showing loading spinner only for the focused block).
  function attachActiveHandlers(key: string) {
    return {
      onFocusCapture: () => setActiveMunicipalityKey(key),
      onBlurCapture: (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setActiveMunicipalityKey((cur) => (cur === key ? null : cur));
        }
      },
    };
  }

  const getMunicipalitySummary = React.useCallback(
    (m: MunicipalityWithLines) => {
      const isLoading = isMunicipalityLoading(m);
      const total = m.Value.length;
      const selected = m.Value.reduce(
        (acc, ln) => acc + (checkedLines.has(ln.LineNrReal) ? 1 : 0),
        0
      );

      return selectionSummary(
        isLoading,
        total,
        selected,
        "närtrafikområden",
        "närtrafikområde"
      );
    },
    [checkedLines]
  );

  if (loadingMunicipalities) {
    return (
      <div className="flex gap-4 p-4 justify-center items-center">
        <Spinner className="w-6 h-6" />
        <div className="text-md">Laddar kommuner…</div>
      </div>
    );
  }

  if (error && municipalities.length === 0) {
    return (
      <div className={className} style={style}>
        <div className="p-4 pb-6">
          <Alert
            variant="error"
            onDismiss={() => setError(null)}
            onOpenChange={() => {}}
          >
            <AlertTitle>{error}</AlertTitle>
            <AlertDescription>
              Kontrollera din uppkoppling eller försök igen senare.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <div className="p-4 pb-6 pt-0">
        <Heading>Närtrafik</Heading>
        <p>
          Länet är indelat i olika närtrafikområden. I varje område finns
          särskilda närtrafikhållplatser som du kan resa till med närtrafiken. I
          rullistan för varje kommun väljer du vilka områden du vill se i
          kartan.
        </p>
        {error && municipalities.length > 0 && (
          <div className="mb-3">
            <Alert
              variant="error"
              onDismiss={() => setError(null)}
              onOpenChange={() => {}}
            >
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {municipalities.map((m, index) => {
            const selectId = `${baseId}-${m.Key}-select`;
            const labelId = `${baseId}-${m.Key}-label`;
            const summary = getMunicipalitySummary(m);
            const isLast = index === municipalities.length - 1;
            const municipalLoading = isMunicipalityLoading(m);
            const showSpinner =
              municipalLoading &&
              (activeMunicipalityKey ? activeMunicipalityKey === m.Key : true);

            return (
              <div key={m.Key} {...attachActiveHandlers(m.Key)}>
                <div className="flex items-center gap-2 h-6 mb-1">
                  <Label
                    id={labelId}
                    htmlFor={selectId}
                    className="truncate mb-0"
                  >
                    {m.Key}
                  </Label>
                  {showSpinner && <Spinner className="ml-auto w-6 h-6" />}
                </div>

                <MultiSelect
                  id={selectId}
                  options={buildOptionsForMunicipality(m)}
                  value={selectedValuesForMunicipality(m)}
                  onValueChange={(vals) => onMunicipalityChange(m, vals)}
                  placeholder={summary}
                  ariaLabelledBy={labelId}
                  showCounter={false}
                  side={isLast ? "top" : "bottom"}
                  className="bg-white"
                  size="default"
                />
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          variant="secondary"
          showIcon
          iconPosition="left"
          iconName="trashcan"
          onClick={handleClearZones}
          disabled={selectedZones.length === 0}
          className="mt-4 w-full"
        >
          Rensa alla val
        </Button>
      </div>
    </div>
  );
}
